import React, { useState } from 'react';
import { base44 } from '@/api/base44Client';
import {
  Loader2, Upload, CheckCircle, AlertCircle, Link2, FileSpreadsheet,
  X, Plus, Search, ExternalLink, Building2,
} from 'lucide-react';
import { useToast } from '@/components/ui/use-toast';

// Sage invoice reconciliation tab for the B2B portal.
// Upload a Sage export (CSV/Excel), the backend matches each invoice to an
// existing B2BOrder by invoice number / PO / company+total. Unmatched invoices
// are listed and can be saved as "unallocated" orders so they stay tracked.

export default function InvoiceReconciliationTab({ accounts, onOrdersChanged, onAccountsChanged }) {
  const { toast } = useToast();
  const [dragging, setDragging] = useState(false);
  const [parsing, setParsing] = useState(false);
  const [result, setResult] = useState(null); // { matched, unmatched, total }
  const [confirming, setConfirming] = useState(null); // id being confirmed
  const [creating, setCreating] = useState(null); // invoice index being created
  const [search, setSearch] = useState('');
  const [importingLines, setImportingLines] = useState(false);

  const handleFile = async (file) => {
    if (!file) return;
    const okTypes = ['.csv', '.xls', '.xlsx', '.txt', '.pdf'];
    const isOk = okTypes.some(ext => file.name.toLowerCase().endsWith(ext)) || file.type.includes('csv') || file.type.includes('sheet') || file.type.includes('excel') || file.type.includes('pdf');
    if (!isOk) { toast({ title: 'Upload a CSV, Excel or PDF file from Sage', variant: 'destructive' }); return; }
    setParsing(true);
    setResult(null);
    try {
      const upRes = await base44.integrations.Core.UploadFile({ file });
      if (!upRes?.file_url) throw new Error('Upload failed');
      const res = await base44.functions.invoke('b2bReconcileInvoices', { file_url: upRes.file_url });
      if (!res?.data?.success) {
        const errMsg = res?.data?.error || 'Reconciliation failed';
        const headers = res?.data?.raw_headers;
        throw new Error(headers ? `${errMsg} (columns found: ${headers.join(', ')})` : errMsg);
      }
      setResult(res.data);
      toast({
        title: 'Reconciliation complete',
        description: `${res.data.matched.length} matched · ${res.data.unmatched.length} unmatched of ${res.data.total}`,
      });
    } catch (e) {
      toast({ title: 'Reconciliation failed', description: e.message, variant: 'destructive' });
    }
    setParsing(false);
  };

  const onDrop = (e) => { e.preventDefault(); setDragging(false); handleFile(e.dataTransfer.files?.[0]); };

  // Import a Sage invoice line-item export (Invoice No · Product Code ·
  // Description · Qty · Net · VAT · Gross) to populate real line items on
  // matched consumer and unallocated B2B invoices.
  const handleLineItemsFile = async (file) => {
    if (!file) return;
    setImportingLines(true);
    try {
      const upRes = await base44.integrations.Core.UploadFile({ file });
      if (!upRes?.file_url) throw new Error('Upload failed');
      const res = await base44.functions.invoke('importSageLineItems', { file_url: upRes.file_url });
      if (!res?.data?.success) throw new Error(res?.data?.error || 'Import failed');
      toast({
        title: 'Line items imported',
        description: `${res.data.orders_updated} consumer · ${res.data.b2b_updated} B2B updated${res.data.unmatched.length ? ` · ${res.data.unmatched.length} unmatched` : ''}`,
      });
      if (onOrdersChanged) onOrdersChanged();
    } catch (e) {
      toast({ title: 'Line item import failed', description: e.message, variant: 'destructive' });
    }
    setImportingLines(false);
  };

  // Confirm a match — saves the Sage Invoice.Number + Invoice.Date onto the
  // linked order so the audit view matches Sage exactly. Works for both B2B
  // orders and consumer/retail Orders.
  const confirmMatch = async (inv, order, matchType) => {
    setConfirming(order.id);
    try {
      if (matchType === 'retail') {
        await base44.entities.Order.update(order.id, {
          sage_invoice_number: inv.invoice_number,
          sage_invoice_date: inv.invoice_date || null,
        });
      } else {
        await base44.entities.B2BOrder.update(order.id, {
          sage_invoice_number: inv.invoice_number,
          sage_invoice_date: inv.invoice_date || null,
          status: order.status === 'unallocated' ? 'invoiced' : order.status,
        });
      }
      toast({ title: 'Match confirmed', description: `Sage ${inv.invoice_number} linked to ${order.company_name}` });
      setResult(prev => ({
        ...prev,
        matched: prev.matched.map(m => m.order.id === order.id ? { ...m, confirmed: true } : m),
      }));
    } catch (e) {
      toast({ title: 'Confirm failed', description: e.message, variant: 'destructive' });
    }
    setConfirming(null);
  };

  // Create a new B2B account directly from the Sage invoice, then assign it.
  // Reuses an existing account with the same account code if one already
  // exists, so re-uploading the same Sage export never creates duplicates.
  const createAccountFromInvoice = async (inv) => {
    try {
      // Auto-derive Sage-style account code (first 4 letters of name + 001) when Sage didn't supply one
      const deriveCode = (name) => {
        const base = String(name || '').replace(/[^A-Za-z0-9]/g, '').slice(0, 4).toUpperCase();
        return base ? `${base}001` : '';
      };
      const code = inv.account_code || deriveCode(inv.company_name);
      if (code) {
        const existing = accounts.find(a => a.account_code && a.account_code.toLowerCase() === code.toLowerCase());
        if (existing) {
          toast({ title: 'Using existing account', description: `${existing.company_name} (${code})` });
          return existing.id;
        }
      }
      const created = await base44.entities.B2BAccount.create({
        company_name: inv.company_name || 'Unknown',
        account_code: code,
        active: true,
      });
      if (onAccountsChanged) onAccountsChanged(created);
      toast({ title: 'Account created', description: created.company_name });
      return created.id;
    } catch (e) {
      toast({ title: 'Create account failed', description: e.message, variant: 'destructive' });
      return null;
    }
  };

  // Persist a dismissal so the retail / non-B2B invoice doesn't reappear on
  // the next Sage upload. Toggling again undoes it (deletes the record).
  const dismissInvoice = async (inv) => {
    if (!inv.invoice_number) {
      toast({ title: 'Invoice has no number to dismiss', variant: 'destructive' });
      return;
    }
    const isDismissed = (result.unmatched.find(u => u.invoice.invoice_number === inv.invoice_number) || {}).dismissed;
    if (isDismissed) {
      // Undo — find and delete the dismissal record
      try {
        const existing = await base44.entities.ReconciliationDismissal.filter({ invoice_number: inv.invoice_number });
        if (existing?.length) await base44.entities.ReconciliationDismissal.delete(existing[0].id);
        setResult(prev => ({ ...prev, unmatched: prev.unmatched.map(u => u.invoice.invoice_number === inv.invoice_number ? { ...u, dismissed: false } : u) }));
      } catch (e) {
        toast({ title: 'Undo failed', description: e.message, variant: 'destructive' });
      }
      return;
    }
    try {
      await base44.entities.ReconciliationDismissal.create({
        invoice_number: inv.invoice_number,
        company_name: inv.company_name || '',
        total_amount: inv.total_amount ?? null,
        dismissed_at: new Date().toISOString(),
      });
      setResult(prev => ({ ...prev, unmatched: prev.unmatched.map(u => u.invoice.invoice_number === inv.invoice_number ? { ...u, dismissed: true } : u) }));
    } catch (e) {
      toast({ title: 'Dismiss failed', description: e.message, variant: 'destructive' });
    }
  };

  // Save the unallocated order to the selected account if one is chosen,
  // otherwise create (or reuse) an account from the invoice first.
  const createAndAssign = async (inv, selectedId) => {
    setCreating(inv.invoice_number);
    const newId = selectedId || await createAccountFromInvoice(inv);
    if (newId) await createUnallocated(inv, newId);
    setCreating(null);
  };

  // Create an "unallocated" order for an unmatched invoice so it's tracked
  const createUnallocated = async (inv, accountId) => {
    setCreating(inv.invoice_number);
    try {
      const account = accounts.find(a => a.id === accountId);
      const subtotal = inv.net_amount || (inv.total_amount && inv.vat_amount ? inv.total_amount - inv.vat_amount : 0);
      const vat = inv.vat_amount || (inv.total_amount && subtotal ? inv.total_amount - subtotal : 0);
      await base44.entities.B2BOrder.create({
        b2b_account_id: accountId,
        company_name: account?.company_name || inv.company_name || 'Unknown',
        po_number: inv.po_number || inv.reference || '',
        po_date: inv.invoice_date || null,
        status: 'unallocated',
        line_items: [{
          item_no: 1,
          qty: 1,
          product_code: '',
          description: `Sage invoice ${inv.invoice_number} — awaiting allocation`,
          unit_price: subtotal,
          extended_price: subtotal,
          stock_allocated: false,
        }],
        subtotal_ex_vat: subtotal,
        vat_amount: vat,
        total_inc_vat: inv.total_amount || (subtotal + vat),
        sage_invoice_number: inv.invoice_number,
        sage_invoice_date: inv.invoice_date || null,
        notes: `Imported from Sage reconciliation on ${new Date().toLocaleDateString('en-GB')}. No matching PO found — needs manual allocation.`,
      });
      toast({ title: 'Unallocated order created', description: `Sage ${inv.invoice_number} saved for later allocation` });
      setResult(prev => ({
        ...prev,
        unmatched: prev.unmatched.map(u => u.invoice.invoice_number === inv.invoice_number ? { ...u, created: true } : u),
      }));
      if (onOrdersChanged) onOrdersChanged();
    } catch (e) {
      toast({ title: 'Create failed', description: e.message, variant: 'destructive' });
    }
    setCreating(null);
  };

  const filteredMatched = (result?.matched || []).filter(m => {
    if (!search) return true;
    const q = search.toLowerCase();
    return m.invoice.invoice_number?.toLowerCase().includes(q) || m.order.company_name?.toLowerCase().includes(q) || m.order.po_number?.toLowerCase().includes(q);
  });
  const filteredUnmatched = (result?.unmatched || []).filter(u => {
    if (!search) return true;
    const q = search.toLowerCase();
    return u.invoice.invoice_number?.toLowerCase().includes(q) || u.invoice.company_name?.toLowerCase().includes(q);
  });

  return (
    <div className="space-y-6">
      {/* Info banner */}
      <div className="border border-white/10 bg-white/[0.02] p-4">
        <p className="text-white/50 text-xs leading-relaxed">
          <span className="text-white/70 font-bold">How it works:</span> Export your sales invoices <span className="text-white/70">or sales order list</span> from Sage as CSV, Excel, or PDF, then drop the file below.
          Each row is matched to an existing B2B order <span className="text-white/70">or retail customer order</span> by invoice/order number, PO number, or company / customer name + total.
          Rows that can't be allocated are saved as <span className="text-yellow-400 font-bold">"unallocated"</span> orders so they stay tracked and can be matched later — nothing is lost.
        </p>
      </div>

      {/* Drop zone */}
      <div
        onDragOver={(e) => { e.preventDefault(); setDragging(true); }}
        onDragLeave={() => setDragging(false)}
        onDrop={onDrop}
        className={`border-2 border-dashed rounded transition-colors p-8 text-center ${dragging ? 'border-primary bg-primary/5' : 'border-white/15 hover:border-white/30'}`}
      >
        {parsing ? (
          <div className="flex flex-col items-center gap-3">
            <Loader2 className="w-8 h-8 animate-spin text-primary" />
            <p className="text-white/50 text-sm font-bold tracking-widest">RECONCILING INVOICES…</p>
            <p className="text-white/30 text-xs">Extracting invoices from your Sage file and matching against B2B orders</p>
          </div>
        ) : (
          <label className="cursor-pointer flex flex-col items-center gap-3">
            <FileSpreadsheet className="w-8 h-8 text-white/30" />
            <p className="text-white/70 font-bold text-sm tracking-widest">DROP YOUR SAGE EXPORT HERE</p>
            <p className="text-white/30 text-xs">Invoice list or sales order list — CSV, Excel, or PDF from Sage</p>
            <input type="file" accept=".csv,.xls,.xlsx,.txt,.pdf" className="hidden" onChange={(e) => { handleFile(e.target.files?.[0]); e.target.value = ''; }} />
          </label>
        )}
      </div>

      {/* Line-item importer */}
      <div className="border border-cyan-400/20 bg-cyan-400/[0.02] p-4 flex items-center gap-4 flex-wrap">
        <FileSpreadsheet className="w-5 h-5 text-cyan-400 shrink-0" />
        <div className="flex-1 min-w-0">
          <p className="text-cyan-400 text-[10px] font-bold tracking-widest mb-0.5">SAGE LINE-ITEM IMPORT</p>
          <p className="text-white/40 text-xs">Upload a Sage invoice line-item export (Invoice No · Product Code · Description · Qty · Net · VAT · Gross) to populate real line items on matched invoices so they render a full breakdown.</p>
        </div>
        <label className={`cursor-pointer inline-flex items-center gap-1.5 border border-cyan-400/30 bg-cyan-400/10 hover:bg-cyan-400 hover:text-black text-cyan-400 font-bold text-[10px] tracking-widest px-3 py-2 transition-colors ${importingLines ? 'opacity-50 pointer-events-none' : ''}`}>
          {importingLines ? <Loader2 className="w-3.5 h-3.5 animate-spin" /> : <Upload className="w-3.5 h-3.5" />} {importingLines ? 'IMPORTING…' : 'UPLOAD LINE ITEMS'}
          <input type="file" accept=".csv,.xls,.xlsx,.txt" className="hidden" onChange={(e) => { handleLineItemsFile(e.target.files?.[0]); e.target.value = ''; }} />
        </label>
      </div>

      {/* Results */}
      {result && (
        <div className="space-y-6">
          {/* Summary */}
          <div className="grid grid-cols-2 sm:grid-cols-4 gap-3">
            <div className="border border-white/10 bg-[#111115] p-4 text-center">
              <p className="text-white/30 text-[10px] font-bold tracking-widest mb-1">TOTAL</p>
              <p className="text-white font-black text-2xl">{result.total}</p>
            </div>
            <div className="border border-green-400/20 bg-green-400/[0.03] p-4 text-center">
              <p className="text-green-400/70 text-[10px] font-bold tracking-widest mb-1">MATCHED</p>
              <p className="text-green-400 font-black text-2xl">{result.matched.length}</p>
              <p className="text-white/30 text-[9px] mt-1">{result.b2b_matched || 0} B2B · {result.retail_matched || 0} retail</p>
            </div>
            <div className="border border-yellow-400/20 bg-yellow-400/[0.03] p-4 text-center">
              <p className="text-yellow-400/70 text-[10px] font-bold tracking-widest mb-1">UNMATCHED</p>
              <p className="text-yellow-400 font-black text-2xl">{result.unmatched.length}</p>
            </div>
            <div className="border border-white/15 bg-white/[0.02] p-4 text-center">
              <p className="text-white/40 text-[10px] font-bold tracking-widest mb-1">PREVIOUSLY SKIPPED</p>
              <p className="text-white/60 font-black text-2xl">{result.dismissed_count || 0}</p>
              <p className="text-white/30 text-[9px] mt-1">retail / non-B2B</p>
            </div>
          </div>

          {/* Search */}
          <input
            value={search}
            onChange={(e) => setSearch(e.target.value)}
            placeholder="Search by invoice number, company, PO…"
            className="seo-input"
          />

          {/* Matched */}
          {filteredMatched.length > 0 && (
            <div>
              <p className="text-green-400 text-[10px] font-bold tracking-widest mb-3 flex items-center gap-2">
                <CheckCircle className="w-3.5 h-3.5" /> MATCHED INVOICES ({filteredMatched.length})
              </p>
              <div className="space-y-2">
                {filteredMatched.map((m, idx) => (
                  <MatchedRow key={idx} match={m} confirming={confirming === m.order.id} onConfirm={() => confirmMatch(m.invoice, m.order, m.match_type)} />
                ))}
              </div>
            </div>
          )}

          {/* Unmatched */}
          {filteredUnmatched.length > 0 && (
            <div>
              <p className="text-yellow-400 text-[10px] font-bold tracking-widest mb-3 flex items-center gap-2">
                <AlertCircle className="w-3.5 h-3.5" /> UNMATCHED INVOICES ({filteredUnmatched.length})
              </p>
              <p className="text-white/30 text-xs mb-3">Save these as unallocated orders so they stay tracked, or match them manually later.</p>
              <div className="space-y-2">
                {filteredUnmatched.map((u, idx) => (
                  <UnmatchedRow key={idx} unmatched={u} accounts={accounts} creating={creating === u.invoice.invoice_number} onCreate={(aid) => createUnallocated(u.invoice, aid)} onCreateFromInvoice={(selId) => createAndAssign(u.invoice, selId)} onDismiss={() => dismissInvoice(u.invoice)} />
                ))}
              </div>
            </div>
          )}

          {filteredMatched.length === 0 && filteredUnmatched.length === 0 && (
            <p className="text-white/30 text-xs text-center py-6">No results match your search.</p>
          )}
        </div>
      )}
    </div>
  );
}

function MatchedRow({ match, confirming, onConfirm }) {
  const { invoice, order, match_method, match_type, confirmed } = match;
  const isRetail = match_type === 'retail';
  const typeBadge = isRetail
    ? 'border-blue-400/30 text-blue-400'
    : 'border-primary/30 text-primary';
  return (
    <div className="border border-green-400/20 bg-green-400/[0.02] p-4 flex items-center gap-4 flex-wrap">
      <Link2 className="w-4 h-4 text-green-400 shrink-0" />
      <div className="flex-1 min-w-0">
        <div className="flex items-center gap-2 flex-wrap">
          <p className="text-white font-bold text-sm">Sage {invoice.invoice_number}</p>
          <span className="text-green-400/60 text-[10px] font-bold tracking-widest border border-green-400/30 px-1.5 py-0.5">MATCHED · {match_method}</span>
          <span className={`text-[10px] font-bold tracking-widest border px-1.5 py-0.5 ${typeBadge}`}>{isRetail ? 'RETAIL' : 'B2B'}</span>
        </div>
        <p className="text-white/40 text-xs mt-0.5">
          {invoice.company_name} · £{(invoice.total_amount || 0).toFixed(2)} · {invoice.invoice_date || ''}
          {invoice.sage_order_number && invoice.sage_order_number !== invoice.invoice_number ? <span className="ml-2 text-white/30">Order: {invoice.sage_order_number}</span> : null}
          {invoice.po_number ? <span className="ml-2 text-white/30">PO: {invoice.po_number}</span> : null}
        </p>
        <p className="text-green-400/70 text-xs mt-0.5">
          → {order.company_name}{order.po_number ? ` · PO ${order.po_number}` : ''} · {order.invoice_number || 'no invoice yet'} · £{(order.total_inc_vat || 0).toFixed(2)}
        </p>
      </div>
      {confirmed ? (
        <div className="flex items-center gap-2">
          <span className="text-green-400 text-[10px] font-bold tracking-widest flex items-center gap-1"><CheckCircle className="w-3.5 h-3.5" /> LINKED</span>
          {isRetail && (
            <a href={`/admin/orders`} className="inline-flex items-center gap-1.5 border border-blue-400/30 bg-blue-400/10 hover:bg-blue-400 hover:text-black text-blue-400 font-bold text-[10px] tracking-widest px-3 py-2 transition-colors">
              <ExternalLink className="w-3.5 h-3.5" /> VIEW ORDER
            </a>
          )}
        </div>
      ) : (
        <button
          onClick={onConfirm}
          disabled={confirming}
          className="inline-flex items-center gap-1.5 border border-green-400/30 bg-green-400/10 hover:bg-green-400 hover:text-black text-green-400 font-bold text-[10px] tracking-widest px-3 py-2 transition-colors disabled:opacity-50"
        >
          {confirming ? <Loader2 className="w-3.5 h-3.5 animate-spin" /> : <CheckCircle className="w-3.5 h-3.5" />} CONFIRM LINK
        </button>
      )}
    </div>
  );
}

function UnmatchedRow({ unmatched, accounts, creating, onCreate, onCreateFromInvoice, onDismiss }) {
  const { invoice, suggested_account, created, dismissed } = unmatched;
  const [selectedAccount, setSelectedAccount] = useState(suggested_account?.id || '');

  if (dismissed) {
    return (
      <div className="border border-white/10 bg-white/[0.02] p-4 opacity-50">
        <div className="flex items-center gap-4">
          <CheckCircle className="w-4 h-4 text-white/30 shrink-0" />
          <div className="flex-1 min-w-0">
            <p className="text-white/50 font-bold text-sm">Sage {invoice.invoice_number || '—'} — skipped</p>
            <p className="text-white/30 text-xs mt-0.5">{invoice.company_name || 'Unknown'} · retail / non-B2B</p>
          </div>
          <button onClick={() => onDismiss && onDismiss(invoice)} className="text-white/30 hover:text-white text-[10px] font-bold tracking-widest">UNDO</button>
        </div>
      </div>
    );
  }

  return (
    <div className="border border-yellow-400/20 bg-yellow-400/[0.02] p-4">
      <div className="flex items-center gap-4 flex-wrap">
        <AlertCircle className="w-4 h-4 text-yellow-400 shrink-0" />
        <div className="flex-1 min-w-0">
          <p className="text-white font-bold text-sm">Sage {invoice.invoice_number || '—'}</p>
          <p className="text-white/40 text-xs mt-0.5">
            {invoice.company_name || 'Unknown company'} {invoice.account_code ? `· ${invoice.account_code}` : ''} · £{(invoice.total_amount || 0).toFixed(2)} · {invoice.invoice_date || ''}
          </p>
          {invoice.po_number && <p className="text-white/30 text-xs mt-0.5">PO ref: {invoice.po_number}</p>}
        </div>
        {created ? (
          <span className="text-cyan-400 text-[10px] font-bold tracking-widest flex items-center gap-1"><CheckCircle className="w-3.5 h-3.5" /> SAVED</span>
        ) : (
          <div className="flex gap-2 flex-wrap">
            <button
              onClick={() => onCreate(selectedAccount)}
              disabled={creating || !selectedAccount}
              className="inline-flex items-center gap-1.5 border border-yellow-400/30 bg-yellow-400/10 hover:bg-yellow-400 hover:text-black text-yellow-400 font-bold text-[10px] tracking-widest px-3 py-2 transition-colors disabled:opacity-50"
            >
              {creating ? <Loader2 className="w-3.5 h-3.5 animate-spin" /> : <Plus className="w-3.5 h-3.5" />} SAVE UNALLOCATED
            </button>
            {invoice.company_name && (
              <button
                onClick={() => onCreateFromInvoice(selectedAccount)}
                disabled={creating}
                className="inline-flex items-center gap-1.5 border border-primary/30 bg-primary/10 hover:bg-primary hover:text-white text-primary font-bold text-[10px] tracking-widest px-3 py-2 transition-colors disabled:opacity-50"
                title={selectedAccount ? 'Save this invoice to the selected account' : `Create a new trade account for "${invoice.company_name}" and save this invoice to it`}
              >
                {creating ? <Loader2 className="w-3.5 h-3.5 animate-spin" /> : <Building2 className="w-3.5 h-3.5" />} {selectedAccount ? 'ASSIGN' : 'CREATE &amp; ASSIGN'}
              </button>
            )}
            <button
              onClick={onDismiss}
              disabled={creating}
              className="inline-flex items-center gap-1.5 border border-white/15 text-white/40 hover:text-white hover:border-white/30 font-bold text-[10px] tracking-widest px-3 py-2 transition-colors disabled:opacity-50"
              title="Skip this invoice — for retail / non-B2B customers already handled elsewhere"
            >
              <X className="w-3.5 h-3.5" /> SKIP
            </button>
          </div>
        )}
      </div>
      {!created && (
        <div className="mt-3 flex items-center gap-2">
          <Search className="w-3.5 h-3.5 text-white/20" />
          <select value={selectedAccount} onChange={e => setSelectedAccount(e.target.value)} className="seo-input flex-1 max-w-xs">
            <option value="">— Assign to account —</option>
            {accounts.map(a => <option key={a.id} value={a.id}>{a.company_name}{a.account_code ? ` (${a.account_code})` : ''}</option>)}
          </select>
          {suggested_account && <span className="text-yellow-400/60 text-[10px] font-bold tracking-widest">SUGGESTED</span>}
        </div>
      )}
    </div>
  );
}